import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useGetCurrentUserQuery } from "../../../../app/accounts/authuser";
import { useMessengerSubscribe } from "../../../../app/messenger/MessengerSocketContext";
import type {
  MessengerIncomingEvent,
  TypingTaskBoardPayload,
} from "../../../../types/messengerEvents";

/**
 * タイトル列を表す column_id の予約値（BE 仕様 websocket-spa.md §3.3）。
 * 動的列の ID と衝突しないよう `__` 接頭辞を持つ。
 */
export const TITLE_PRESENCE_COLUMN_ID = "__title";

/** started 受信からこの時間 stopped が来なければ自動で消す（typing 表示と同じ）。 */
const AUTO_CLEAR_MS = 7000;

/** 編集者なしセルの共有戻り値（毎回新規配列を作って再レンダーを誘発しない）。 */
const EMPTY_EDITORS: readonly number[] = [];

const cellKeyOf = (taskId: string, columnId: string) => `${taskId}:${columnId}`;

/**
 * タスクボードのセル単位「編集中ユーザー」presence を購読するフック。
 *
 * `/ws/spa/events` の `typing.task_board.started / stopped`（scope キーは
 * project_id）を集約し、`taskId + columnId → 編集中 user_id 一覧` を保持する。
 *
 * - started 受信ごとに 7s auto-clear タイマーを更新（stop の取りこぼし対策）。
 * - stopped 受信で即時削除。
 * - 自分自身のイベントは表示しない（サーバ側で送信者除外済みだが安全弁）。
 * - プロジェクト切替・unmount で全クリア。
 */
export function useTaskBoardPresence(projectId: string | undefined): {
  /** 指定セルを編集中の他ユーザー ID 一覧（いなければ空配列）。 */
  // eslint-disable-next-line no-unused-vars -- 関数シグネチャ引数名はドキュメント目的
  resolveCellEditors: (taskId: string, columnId: string) => readonly number[];
  /** 1 セルでも編集中ユーザーがいるか（メンバー名解決クエリの発火条件）。 */
  hasRemoteEditors: boolean;
} {
  const { data: currentUserData } = useGetCurrentUserQuery();
  const currentUserId: number | undefined = currentUserData?.data?.id;
  const currentUserIdRef = useRef<number | undefined>(currentUserId);
  currentUserIdRef.current = currentUserId;

  const [editorsByCell, setEditorsByCell] = useState<Record<string, number[]>>(
    {},
  );
  // key = `${cellKey}|${userId}` の auto-clear タイマー
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(
    new Map(),
  );

  const clearTimer = useCallback((timerKey: string) => {
    const timer = timersRef.current.get(timerKey);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(timerKey);
    }
  }, []);

  const removeEditor = useCallback(
    (cellKey: string, userId: number) => {
      clearTimer(`${cellKey}|${userId}`);
      setEditorsByCell((prev) => {
        const current = prev[cellKey];
        if (!current || !current.includes(userId)) return prev;
        const nextUsers = current.filter((id) => id !== userId);
        const next = { ...prev };
        if (nextUsers.length === 0) delete next[cellKey];
        else next[cellKey] = nextUsers;
        return next;
      });
    },
    [clearTimer],
  );

  const addOrRefreshEditor = useCallback(
    (cellKey: string, userId: number) => {
      const timerKey = `${cellKey}|${userId}`;
      clearTimer(timerKey);
      timersRef.current.set(
        timerKey,
        setTimeout(() => {
          timersRef.current.delete(timerKey);
          setEditorsByCell((prev) => {
            const current = prev[cellKey];
            if (!current || !current.includes(userId)) return prev;
            const nextUsers = current.filter((id) => id !== userId);
            const next = { ...prev };
            if (nextUsers.length === 0) delete next[cellKey];
            else next[cellKey] = nextUsers;
            return next;
          });
        }, AUTO_CLEAR_MS),
      );
      setEditorsByCell((prev) => {
        const current = prev[cellKey];
        if (current?.includes(userId)) return prev;
        return { ...prev, [cellKey]: [...(current ?? []), userId] };
      });
    },
    [clearTimer],
  );

  const handleEvent = useCallback(
    (ev: MessengerIncomingEvent) => {
      if (
        ev.type !== "typing.task_board.started" &&
        ev.type !== "typing.task_board.stopped"
      ) {
        return;
      }
      const payload = ev.payload as TypingTaskBoardPayload;
      const taskId = payload?.task_id;
      const columnId = payload?.column_id;
      const userId = payload?.user_id;
      if (!taskId || !columnId || typeof userId !== "number") return;
      if (
        currentUserIdRef.current != null &&
        userId === currentUserIdRef.current
      ) {
        return;
      }
      const cellKey = cellKeyOf(taskId, columnId);
      if (ev.type === "typing.task_board.started") {
        addOrRefreshEditor(cellKey, userId);
      } else {
        removeEditor(cellKey, userId);
      }
    },
    [addOrRefreshEditor, removeEditor],
  );

  useMessengerSubscribe("task_board", projectId, handleEvent);

  // プロジェクト切替・unmount では過去の presence を引きずらない。
  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach((t) => clearTimeout(t));
      timers.clear();
      setEditorsByCell({});
    };
  }, [projectId]);

  const resolveCellEditors = useCallback(
    (taskId: string, columnId: string): readonly number[] =>
      editorsByCell[cellKeyOf(taskId, columnId)] ?? EMPTY_EDITORS,
    [editorsByCell],
  );

  const hasRemoteEditors = useMemo(
    () => Object.keys(editorsByCell).length > 0,
    [editorsByCell],
  );

  return { resolveCellEditors, hasRemoteEditors };
}
